import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { productService } from "../../lib/api/shopService";
import ProductCard from "./ProductCard";
import useCurrentUser from "../../hooks/useCurrentUser";
import { authService } from "../../lib/api/apiService";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;

const TrendingProducts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser({ forceRefresh: false });

  const queryParams = useMemo(
    () => ({
      status: "published",
      page: 1,
      limit: PAGE_SIZE,
      sortBy: "likesCount",
      sortOrder: "desc",
    }),
    [],
  );

  const { data: productsData, isLoading } = useQuery({
    queryKey: ["home-trending-products", queryParams],
    queryFn: () => productService.getProducts(queryParams),
    enabled: true,
  });

  const products = productsData?.data?.data ?? [];

  const likeProductMutation = useMutation({
    mutationFn: (productId) => productService.likeProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["home-trending-products"] });
    },
  });

  const unlikeProductMutation = useMutation({
    mutationFn: (productId) => productService.unlikeProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["home-trending-products"] });
    },
  });

  const handleToggleFavorite = (productId, isLiked) => {
    if (!authService.isAuthenticated() || !user) {
      setShowLoginModal(true);
      return;
    }

    if (isLiked) {
      unlikeProductMutation.mutate(productId);
    } else {
      likeProductMutation.mutate(productId);
    }
  };

  if (isLoading) {
    return (
      <section className="bg-gray-100 p-4 lg:p-8 rounded-xl">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-hover mb-3" />
          <p className="text-sm text-gray-600">{t("home.trending_products.loading", "Chargement…")}</p>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section className="bg-gray-100 p-4 lg:p-8 rounded-xl">
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
          {t("home.trending_products.title")}
        </h2>
        <Link
          to="/categories"
          className="text-gray-800 hover:text-orange-hover text-sm font-medium flex items-center gap-2 transition-colors w-fit"
        >
          {t("home.trending_products.see_more")}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
        {products.map((product) => (
          <ProductCard
            key={product._id}
            product={product}
            onToggleFavorite={handleToggleFavorite}
          />
        ))}
      </div>

      {showLoginModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {t("home.trending_products.login_required_title", "Connexion requise")}
            </h3>
            <p className="text-gray-700 mb-6">
              {t("home.trending_products.login_required")}
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => setShowLoginModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t("home.trending_products.cancel_button", "Annuler")}
              </button>
              <button
                type="button"
                onClick={() => {
                  navigate("/login");
                  setShowLoginModal(false);
                }}
                className="px-4 py-2 bg-[#E5502E] text-white rounded-lg hover:bg-[#d04426] transition-colors"
              >
                {t("home.trending_products.login_button", "Se connecter")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TrendingProducts;
